import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FilePlus,
  History,
  Upload,
  BookOpen,
  Menu,
  X,
  ScanSearch,
  Building2,
  Edit3,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEstablishment, EstablishmentOnboardingModal } from "@/components/EstablishmentOnboarding";

// V1 = outil documentaire uniquement (hors-MDR)
const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Tableau de bord" },
  { path: "/new-analysis", icon: FilePlus, label: "Nouvelle analyse" },
  { path: "/pdf-import", icon: Upload, label: "Importer documents" },
  { path: "/history", icon: History, label: "Historique" },
  { path: "/reference", icon: BookOpen, label: "Référentiel STOPP/Beers" },
  { path: "/has-reference", icon: BookOpen, label: "Référentiel HAS" },
];

function isAdmin() {
  // Le JWT contient l'email en sub — décodage côté client (non vérifié, juste pour l'UI)
  try {
    const token = sessionStorage.getItem("access_token");
    if (!token) return false;
    const payload = JSON.parse(atob(token.split(".")[1]));
    const admins = (process.env.REACT_APP_ADMIN_EMAILS ?? "").split(",").map(e => e.trim().toLowerCase());
    return admins.includes((payload.sub ?? "").toLowerCase());
  } catch {
    return false;
  }
}

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editEstablishment, setEditEstablishment] = useState(false);
  const location = useLocation();
  const estCtx = useEstablishment();
  const establishment = estCtx?.establishment;
  const admin = isAdmin();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "sidebar",
          sidebarOpen && "open"
        )}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg">
              <ScanSearch className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight">
                GeriaScan
              </h1>
              <p className="text-xs text-slate-500">Outil documentaire • Gériatrie</p>
            </div>
          </Link>
        </div>

        <nav className="p-4 space-y-1" data-testid="main-nav">
          {navItems.filter(item => item.path).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "sidebar-nav-item",
                  isActive && "active",
                  item.highlight && !isActive && "bg-indigo-50 text-indigo-700 border border-indigo-200"
                )}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                aria-label={`Aller à la page ${item.label}`}
              >
                <Icon className={cn("h-5 w-5", item.highlight && !isActive && "text-indigo-600")} />
                <span>{item.label}</span>
                {item.highlight && (
                  <span className="ml-auto text-xs bg-indigo-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>
                )}
              </Link>
            );
          })}
          {/* Lien admin — visible uniquement pour les admins */}
          {admin && (
            <>
              <p className="px-2 pt-3 pb-0.5 text-[10px] uppercase tracking-widest text-purple-400">
                Administration
              </p>
              <Link
                to="/admin/users"
                className={cn(
                  "sidebar-nav-item text-purple-700 hover:bg-purple-50",
                  location.pathname === "/admin/users" && "active"
                )}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-admin-users"
              >
                <ShieldCheck className="h-5 w-5 text-purple-600" />
                <span>Utilisateurs</span>
              </Link>
            </>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 space-y-3">
          {/* Établissement courant */}
          {establishment && (
            <button
              type="button"
              onClick={() => setEditEstablishment(true)}
              className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 hover:bg-slate-50 transition-colors group"
              data-testid="current-establishment-card"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-4 w-4 text-teal-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Établissement</p>
                  <p className="text-xs font-semibold text-slate-800 truncate" data-testid="current-establishment-name">
                    {establishment.name}
                  </p>
                  {establishment.city && (
                    <p className="text-[10px] text-slate-500 truncate">
                      {establishment.city}{establishment.postal_code ? ` · ${establishment.postal_code}` : ""}
                    </p>
                  )}
                </div>
                <Edit3 className="h-3.5 w-3.5 text-slate-400 group-hover:text-teal-600 flex-shrink-0" />
              </div>
            </button>
          )}

          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 border border-teal-100">
            <p className="text-xs text-teal-600 mb-1">Basé sur</p>
            <p className="text-sm font-medium text-teal-800">STOPP/START v3 • Beers 2023</p>
            <p className="text-xs text-teal-600">+ ACB • HAS 2023-2025</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content" data-testid="main-content">
        {/* MDR Disclaimer Banner V1 — sticky, toujours visible */}
        <div
          className="sticky top-0 z-40 flex items-start gap-2 border-l-4 border-blue-500 bg-blue-50 px-6 py-2.5"
          role="note"
          aria-label="Avertissement outil documentaire"
          data-testid="mdr-disclaimer"
        >
          <span className="mt-0.5 flex-shrink-0 text-blue-600" aria-hidden="true">ⓘ</span>
          <p className="text-xs leading-relaxed text-blue-900">
            <strong>GeriaScan est un outil documentaire</strong> d'aide à la révision médicamenteuse.
            Toute décision thérapeutique relève de la{" "}
            <strong>responsabilité exclusive du praticien qualifié</strong>.
            GeriaScan n'est pas un dispositif médical.
          </p>
        </div>

        <div className="pb-6">
          <Outlet />
        </div>
      </main>

      {/* Modal édition établissement (depuis la sidebar) */}
      {editEstablishment && (
        <EstablishmentOnboardingModal
          initialEstablishment={establishment}
          onClose={() => setEditEstablishment(false)}
          onCreated={async () => {
            setEditEstablishment(false);
            await estCtx?.refresh?.();
          }}
        />
      )}
    </div>
  );
};

export default Layout;
